import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    IconButton,
    Divider,
    Stack,
    Alert,
    Autocomplete,
    Switch,
    FormControlLabel,
    Avatar,
    Slide,
    Zoom,
    Collapse
} from '@mui/material';
import { useTheme, alpha, styled } from '@mui/material/styles';
import {
    Close as CloseIcon,
    PersonAdd as PersonAddIcon,
    TrendingUp,
    TrendingDown,
    Warning,
    CheckCircle,
    Person as PersonIcon,
    Business as BusinessIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Description as BioIcon,
    Tag as TagIcon,
    Score as ScoreIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useMutation, useQueryClient } from 'react-query';
import API from '../../../../../API';

// Styled components matching ClientAddEditDialog style
const ModernDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 24,
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.9)})`,
        backdropFilter: "blur(30px)",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 32px 80px ${alpha(theme.palette.common.black, 0.2)}`,
        overflow: 'hidden',
        maxHeight: '95vh',
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        backgroundColor: alpha(theme.palette.background.default, 0.5),
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            transform: 'translateY(-1px)',
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
        }
    },
}));

const ActionButton = styled(Button)(({ theme, variant: buttonVariant, color }) => ({
    borderRadius: 12,
    padding: '12px 24px',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    ...(buttonVariant === 'contained' && {
        boxShadow: `0 4px 16px ${alpha(theme.palette[color || 'primary'].main, 0.3)}`,
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 8px 24px ${alpha(theme.palette[color || 'primary'].main, 0.4)}`,
        }
    }),
}));

const ScoreCard = styled(Card)(({ theme, tier }) => {
    const getTierColor = () => {
        switch (tier) {
            case 'hot': return theme.palette.error.main;
            case 'warm': return theme.palette.warning.main;
            case 'cold': return theme.palette.info.main;
            default: return theme.palette.primary.main;
        }
    };

    return {
        borderRadius: 16,
        border: `2px solid ${alpha(getTierColor(), 0.3)}`,
        background: `linear-gradient(135deg, ${alpha(getTierColor(), 0.05)}, ${alpha(getTierColor(), 0.02)})`,
    };
});

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function LeadConversionDialog({ open, onClose, leadData, onSuccess }) {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [showScoreDetails, setShowScoreDetails] = useState(false);
    const [includeScoreInBio, setIncludeScoreInBio] = useState(true);

    // Form state
    const [formData, setFormData] = useState({
        full_name: '',
        company: '',
        email: '',
        location: '',
        phone_number: '',
        bio: '',
        tags: []
    });

    // Generate suggested client data from lead data
    const generateSuggestedClient = (leadData) => {
        if (!leadData) return null;
        
        const { contact, score } = leadData;
        
        return {
            full_name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
            company: contact.company_name || '',
            email: contact.email || '',
            location: contact.location || '',
            phone_number: contact.phone || '',
            bio: contact.notes || '',
            tags: []
        };
    };

    // Initialize form data when leadData is provided
    useEffect(() => {
        if (leadData) {
            const suggestedData = generateSuggestedClient(leadData);
            if (suggestedData) {
                setFormData(suggestedData);
            }
        }
    }, [leadData]);

    // Convert mutation
    const convertMutation = useMutation(
        (data) => API.crm.convertLeadToClient(leadData?.contact?.id, data),
        {
            onSuccess: (result) => {
                queryClient.invalidateQueries(['qualifiedLeads']);
                queryClient.invalidateQueries(['leadStats']);
                onSuccess?.(result);
                onClose();
            }
        }
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleTagsChange = (_, value) => {
        setFormData(prev => ({ ...prev, tags: value }));
    };

    const handleConvert = () => {
        convertMutation.mutate({
            ...formData,
            include_score_in_bio: includeScoreInBio
        });
    };

    const getTierColor = (tier) => {
        switch (tier) {
            case 'hot': return 'error';
            case 'warm': return 'warning';
            case 'cold': return 'info';
            default: return 'primary';
        }
    };

    const getTierIcon = (tier) => {
        switch (tier) {
            case 'hot': return <TrendingUp />;
            case 'warm': return <TrendingDown />;
            default: return <TrendingDown />;
        }
    };

    if (!open || !leadData) return null;

    const { contact, score } = leadData;
    const isValid = formData.full_name.trim() && formData.company.trim() && formData.email.trim();

    return (
        <ModernDialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            TransitionComponent={Transition}
        >
            {/* Loading Progress */}
            {convertMutation.isLoading && (
                <LinearProgress 
                    sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0,
                        zIndex: 1,
                        borderRadius: '24px 24px 0 0'
                    }} 
                />
            )}

            {/* Header */}
            <DialogTitle
                sx={{
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    py: 3,
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                        sx={{
                            bgcolor: theme.palette.success.main,
                            width: 48,
                            height: 48,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                        }}
                    >
                        <PersonAddIcon />
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h5" fontWeight={700}>
                            Convert Lead to Client
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review lead information and complete the conversion
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        disabled={convertMutation.isLoading}
                        sx={{
                            backgroundColor: alpha(theme.palette.background.paper, 0.8),
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                transform: 'rotate(90deg)',
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 4 }}>
                <Grid container spacing={3}>
                    {/* Left Column: Lead Score Info */}
                    <Grid item xs={12} md={5}>
                        <Stack spacing={2}>
                            {/* Score Summary Card */}
                            <ScoreCard tier={score?.tier}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" fontWeight={600}>
                                            Lead Quality Score
                                        </Typography>
                                        <Chip
                                            icon={getTierIcon(score?.tier)}
                                            label={score?.tier?.toUpperCase()}
                                            color={getTierColor(score?.tier)}
                                            size="small"
                                        />
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Total Score
                                            </Typography>
                                            <Typography variant="body2" fontWeight="bold">
                                                {score?.total_score}/100
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={score?.total_score || 0}
                                            color={getTierColor(score?.tier)}
                                            sx={{ height: 8, borderRadius: 1 }}
                                        />
                                    </Box>

                                    {/* Score Breakdown */}
                                    <Grid container spacing={1} sx={{ mb: 2 }}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Email Quality
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {score?.email_quality}/35
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Recency
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {score?.recency}/20
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Engagement
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {score?.engagement}/25
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">
                                                Profile Complete
                                            </Typography>
                                            <Typography variant="body2" fontWeight={600}>
                                                {score?.profile_completeness}/20
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    {/* Recommendation */}
                                    {score?.recommendation && (
                                        <Alert 
                                            severity="info" 
                                            icon={<ScoreIcon />}
                                            sx={{ borderRadius: 2 }}
                                        >
                                            <Typography variant="body2" fontWeight={500}>
                                                {score.recommendation}
                                            </Typography>
                                        </Alert>
                                    )}

                                    {/* Toggle Details */}
                                    <Button
                                        fullWidth
                                        size="small"
                                        onClick={() => setShowScoreDetails(!showScoreDetails)}
                                        endIcon={showScoreDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        sx={{ mt: 1 }}
                                    >
                                        {showScoreDetails ? 'Hide' : 'Show'} Detailed Analysis
                                    </Button>

                                    {/* Detailed Scoring Info */}
                                    <Collapse in={showScoreDetails}>
                                        <Box sx={{ mt: 2 }}>
                                            <Divider sx={{ my: 2 }} />
                                            
                                            {/* Strengths */}
                                            {score?.reasons && score.reasons.length > 0 && (
                                                <>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                                        Strengths
                                                    </Typography>
                                                    {score.reasons.map((reason, idx) => (
                                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                                                            <CheckCircle 
                                                                fontSize="small" 
                                                                sx={{ mr: 1, mt: 0.25, color: 'success.main', fontSize: 16 }} 
                                                            />
                                                            <Typography variant="caption">
                                                                {reason}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </>
                                            )}

                                            {/* Red Flags */}
                                            {score?.red_flags && score.red_flags.length > 0 && (
                                                <>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1, color: 'error.main' }}>
                                                        Red Flags
                                                    </Typography>
                                                    {score.red_flags.map((flag, idx) => (
                                                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                                                            <Warning 
                                                                fontSize="small" 
                                                                sx={{ mr: 1, mt: 0.25, color: 'error.main', fontSize: 16 }} 
                                                            />
                                                            <Typography variant="caption">
                                                                {flag}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </>
                                            )}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </ScoreCard>

                            {/* CRM Source Info */}
                            <Card sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                                        CRM Information
                                    </Typography>
                                    <Stack spacing={1}>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Source
                                            </Typography>
                                            <Typography variant="body2">
                                                {contact?.contact_source || 'Unknown'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">
                                                Status
                                            </Typography>
                                            <Typography variant="body2">
                                                {contact?.status || 'New'}
                                            </Typography>
                                        </Box>
                                        {contact?.total_proposals > 0 && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Proposals
                                                </Typography>
                                                <Typography variant="body2">
                                                    {contact.total_proposals}
                                                </Typography>
                                            </Box>
                                        )}
                                        {contact?.last_contacted && (
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Last Contacted
                                                </Typography>
                                                <Typography variant="body2">
                                                    {new Date(contact.last_contacted).toLocaleDateString()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* Include Score Toggle */}
                            <Card sx={{ borderRadius: 3 }}>
                                <CardContent>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={includeScoreInBio}
                                                onChange={(e) => setIncludeScoreInBio(e.target.checked)}
                                                color="primary"
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    Include Score in Bio
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Add lead scoring details to client bio for reference
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </CardContent>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* Right Column: Client Form */}
                    <Grid item xs={12} md={7}>
                        <Stack spacing={3}>
                            <Typography variant="h6" fontWeight={600}>
                                Client Information
                            </Typography>

                            {/* Basic Fields */}
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Zoom in timeout={300}>
                                        <StyledTextField
                                            name="full_name"
                                            label="Full Name"
                                            fullWidth
                                            required
                                            value={formData.full_name}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                            }}
                                            error={!formData.full_name.trim()}
                                            helperText={!formData.full_name.trim() ? "Required" : ""}
                                        />
                                    </Zoom>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Zoom in timeout={400}>
                                        <StyledTextField
                                            name="company"
                                            label="Company"
                                            fullWidth
                                            required
                                            value={formData.company}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                            }}
                                            error={!formData.company.trim()}
                                            helperText={!formData.company.trim() ? "Required" : ""}
                                        />
                                    </Zoom>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Zoom in timeout={500}>
                                        <StyledTextField
                                            name="email"
                                            label="Email"
                                            fullWidth
                                            required
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                            }}
                                            error={!formData.email.trim()}
                                            helperText={!formData.email.trim() ? "Required" : ""}
                                        />
                                    </Zoom>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Zoom in timeout={600}>
                                        <StyledTextField
                                            name="phone_number"
                                            label="Phone Number"
                                            fullWidth
                                            value={formData.phone_number}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                            }}
                                            helperText="Recommended for better contact management"
                                        />
                                    </Zoom>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Zoom in timeout={700}>
                                        <StyledTextField
                                            name="location"
                                            label="Location"
                                            fullWidth
                                            value={formData.location}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                                            }}
                                            helperText="Recommended for better contact management"
                                        />
                                    </Zoom>
                                </Grid>

                                <Grid item xs={12}>
                                    <Zoom in timeout={800}>
                                        <StyledTextField
                                            name="bio"
                                            label="Bio / Notes"
                                            fullWidth
                                            multiline
                                            rows={4}
                                            value={formData.bio}
                                            onChange={handleChange}
                                            InputProps={{
                                                startAdornment: <BioIcon sx={{ mr: 1, color: 'text.secondary', alignSelf: 'flex-start', mt: 1 }} />,
                                            }}
                                            helperText={includeScoreInBio ? "Lead scoring info will be appended automatically" : "Add any notes about this client"}
                                        />
                                    </Zoom>
                                </Grid>
                            </Grid>

                            {/* Tags */}
                            <Zoom in timeout={900}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TagIcon fontSize="small" color="primary" />
                                        Tags
                                    </Typography>
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[]}
                                        value={formData.tags}
                                        onChange={handleTagsChange}
                                        renderTags={(value, getTagProps) =>
                                            value.map((opt, i) => (
                                                <Chip
                                                    key={opt}
                                                    variant="outlined"
                                                    size="small"
                                                    label={opt}
                                                    color="primary"
                                                    sx={{ borderRadius: 2 }}
                                                    {...getTagProps({ index: i })}
                                                />
                                            ))
                                        }
                                        renderInput={params => (
                                            <StyledTextField
                                                {...params}
                                                placeholder="Add tags (auto-tagged: from-crm, source, score tier)"
                                                size="small"
                                            />
                                        )}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        Auto-added: from-crm, source:{contact?.contact_source}, score:{score?.tier}
                                    </Typography>
                                </Box>
                            </Zoom>
                        </Stack>
                    </Grid>
                </Grid>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ 
                px: 4, 
                py: 3, 
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.7)})`,
                gap: 2
            }}>
                <ActionButton 
                    onClick={onClose} 
                    disabled={convertMutation.isLoading}
                    sx={{ 
                        color: 'text.secondary',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                        }
                    }}
                >
                    Cancel
                </ActionButton>
                
                <ActionButton
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    disabled={!isValid || convertMutation.isLoading}
                    onClick={handleConvert}
                    color="success"
                >
                    {convertMutation.isLoading ? "Converting..." : "Convert to Client"}
                </ActionButton>
            </DialogActions>
        </ModernDialog>
    );
}